import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, ProgressBar } from '../components';
import { plural } from '../format';
import { navigate } from '../router';
import { adminApi, errorText, type CustomerDetail, type CustomerSummary, type ProductRow } from './api';
import { Card, Field, SaveBar, Tag, confirmAction, formatDateTime, useSaver } from './ui';

/** Kundenliste mit Suche (Konzept 5.5) und Kundenkarte: Zugänge, Fortschritt, Geräte, Mails. */
export function CustomersPage() {
  const [query, setQuery] = useState('');
  const [list, setList] = useState<CustomerSummary[] | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => void adminApi.listCustomers(query).then(setList).catch(() => setList([])), 250);
    return () => clearTimeout(timer);
  }, [query]);

  async function create(e: FormEvent) {
    e.preventDefault();
    try {
      const c = await adminApi.createCustomer(email.trim(), name.trim() || null);
      navigate(`/verwaltung/kunden/${c.id}`);
    } catch (err) {
      setError(errorText(err, 'Bitte eine gültige E-Mail-Adresse angeben.'));
    }
  }

  return (
    <>
      <div className="adm-head">
        <div>
          <h1>Kunden</h1>
          <p className="muted">Wer Zugang hat, wie weit sie sind, wann sie zuletzt da waren.</p>
        </div>
      </div>
      <Card as="form" onSubmit={create} title="Kunden anlegen">
        <p className="muted small">Für Freischaltungen von Hand – etwa Coaching-Teilnehmer oder Testzugänge. Wer kauft, wird automatisch angelegt.</p>
        <div className="row">
          <input type="email" placeholder="E-Mail-Adresse" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <input type="text" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
          <button type="submit" className="button" disabled={!email.trim()}>Anlegen</button>
        </div>
        {error && <div className="error">{error}</div>}
      </Card>
      <div className="row" style={{ marginBottom: 12 }}>
        <input type="search" placeholder="Suchen nach Name oder E-Mail …" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      {list === null ? (
        <p className="muted">Wird geladen …</p>
      ) : list.length === 0 ? (
        <p className="muted">{query ? 'Niemand gefunden.' : 'Noch keine Kunden.'}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Kunde</th>
              <th>Zugänge</th>
              <th>Erledigt</th>
              <th>Zuletzt da</th>
              <th>Seit</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link href={`/verwaltung/kunden/${c.id}`} className="table__link">
                    <strong>{c.name ?? c.email}</strong>
                    {c.name && <span className="muted small"> · {c.email}</span>}
                  </Link>
                </td>
                <td>{c.entitlements}</td>
                <td>{plural(c.completedLessons, 'Lektion', 'Lektionen')}</td>
                <td className="muted">{formatDateTime(c.lastSeenAt)}</td>
                <td className="muted">{formatDateTime(c.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function CustomerPage({ id }: { id: string }) {
  const [customer, setCustomer] = useState<CustomerDetail | null | undefined>(undefined);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const reload = useCallback(() => void adminApi.getCustomer(id).then(setCustomer).catch(() => setCustomer(null)), [id]);
  useEffect(() => {
    reload();
    void adminApi.listProducts().then(setProducts).catch(() => undefined);
  }, [reload]);

  if (customer === undefined) return <p className="muted">Wird geladen …</p>;
  if (customer === null)
    return (
      <>
        <h1>Kunde nicht gefunden</h1>
        <Link href="/verwaltung/kunden" className="button button--secondary">Zur Kundenliste</Link>
      </>
    );

  return (
    <>
      <div className="adm-head">
        <div>
          <Link href="/verwaltung/kunden" className="muted small">← Alle Kunden</Link>
          <h1>{customer.name ?? customer.email}</h1>
          <p className="muted">
            {customer.email} · Kunde seit {formatDateTime(customer.createdAt)} · zuletzt da {formatDateTime(customer.lastSeenAt)}
          </p>
        </div>
      </div>
      <Entitlements customer={customer} products={products} onChanged={setCustomer} />
      <Card title="Fortschritt">
        {customer.courses.length === 0 ? (
          <p className="muted small">Kein Kurs freigeschaltet.</p>
        ) : (
          <div className="progress-list">
            {customer.courses.map((c) => (
              <div key={c.id} className="progress-list__row">
                <div>
                  <strong>{c.title.replace('\n', ' ')}</strong>
                  <span className="muted small"> · {c.completed} von {c.lessons} Lektionen · zuletzt {formatDateTime(c.lastSeenAt)}</span>
                </div>
                <div className="progress-list__bar">
                  <ProgressBar percent={c.percent} thin />
                  <span className="small">{c.percent} %</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <CustomerBasics customer={customer} onChanged={setCustomer} />
      <Card title="Geräte und Mails">
        <div className="grid2">
          <div>
            <span className="kicker">Angemeldete Geräte</span>
            {customer.sessions.length === 0 ? <p className="muted small">Keine aktive Sitzung.</p> : (
              <ul className="plain-list">
                {customer.sessions.map((s) => (
                  <li key={s.id} className="small">
                    {s.deviceLabel ?? 'Unbekanntes Gerät'} <span className="muted">· zuletzt {formatDateTime(s.lastSeenAt)}{s.lastPlayAt ? `, Video ${formatDateTime(s.lastPlayAt)}` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <span className="kicker">Letzte Mails</span>
            {customer.mails.length === 0 ? <p className="muted small">Noch keine Mail.</p> : (
              <ul className="plain-list">
                {customer.mails.map((m, i) => (
                  <li key={i} className="small">
                    {MAIL_LABEL[m.kind] ?? m.kind} <Tag tone={m.status === 'sent' ? 'ok' : m.status === 'failed' ? 'warn' : 'muted'}>{MAIL_STATUS[m.status] ?? m.status}</Tag> <span className="muted">{formatDateTime(m.sentAt ?? m.sendAt)}</span>
                    {m.lastError && <div className="error-inline small">{m.lastError}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Card>
    </>
  );
}

const MAIL_LABEL: Record<string, string> = { login: 'Anmelde-Link', welcome: 'Willkommen', welcome_free: 'Willkommen (gratis)', module_release: 'Neues Modul', reminder: 'Erinnerung', completed: 'Glückwunsch', account_deleted: 'Konto gelöscht' };
const MAIL_STATUS: Record<string, string> = { queued: 'wartet', sent: 'gesendet', failed: 'gescheitert', cancelled: 'abgebrochen', skipped: 'übersprungen' };
const SOURCE: Record<string, string> = { purchase: 'Kauf', manual: 'von Hand', free: 'gratis' };

function Entitlements({ customer, products, onChanged }: { customer: CustomerDetail; products: ProductRow[]; onChanged: (c: CustomerDetail) => void }) {
  const [productId, setProductId] = useState('');
  const [note, setNote] = useState('');
  const [sendWelcome, setSendWelcome] = useState(true);
  const saver = useSaver();
  const [linkSent, setLinkSent] = useState(false);
  const available = products.filter((p) => p.status !== 'archived');

  return (
    <Card
      title="Zugänge"
      right={
        <button type="button" className="button button--secondary button--small" onClick={() => void saver.run(async () => { await adminApi.sendLoginLink(customer.id); setLinkSent(true); onChanged(await adminApi.getCustomer(customer.id)); })}>
          {linkSent ? 'Anmelde-Link verschickt' : 'Anmelde-Link schicken'}
        </button>
      }
    >
      {customer.entitlements.length === 0 ? (
        <p className="muted small">Noch kein Zugang.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Produkt</th>
              <th>Woher</th>
              <th>Seit</th>
              <th>Bis</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {customer.entitlements.map((e) => (
              <tr key={e.id} className={e.revokedAt ? 'row--muted' : ''}>
                <td>
                  <strong>{e.product.title}</strong>
                  {e.note && <span className="muted small"> · {e.note}</span>}
                </td>
                <td>{SOURCE[e.source] ?? e.source}</td>
                <td className="muted">{formatDateTime(e.grantedAt)}</td>
                <td className="muted">{e.revokedAt ? <>entzogen {formatDateTime(e.revokedAt)}{e.revokeReason ? ` (${e.revokeReason})` : ''}</> : e.expiresAt ? formatDateTime(e.expiresAt) : 'unbefristet'}</td>
                <td>
                  {e.revokedAt ? (
                    <button type="button" className="link-button small" onClick={() => void saver.run(async () => onChanged(await adminApi.restore(e.id)))}>Wieder freischalten</button>
                  ) : (
                    <button type="button" className="link-button small link-button--danger" onClick={() => { const reason = window.prompt('Zugang entziehen – Grund (optional):', ''); if (reason !== null) void saver.run(async () => onChanged(await adminApi.revoke(e.id, reason || null))); }}>Entziehen</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form
        className="grant"
        onSubmit={(e) => {
          e.preventDefault();
          if (!productId) return;
          void saver.run(async () => {
            onChanged(await adminApi.grant(customer.id, productId, note.trim() || null, sendWelcome));
            setProductId('');
            setNote('');
          });
        }}
      >
        <span className="kicker">Von Hand freischalten</span>
        <div className="row">
          <select value={productId} onChange={(e) => setProductId(e.target.value)} required>
            <option value="">Produkt wählen …</option>
            {available.map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
          <input type="text" placeholder="Notiz, z. B. „Coaching-Teilnehmer“" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          <button type="submit" className="button" disabled={!productId || saver.busy}>Freischalten</button>
        </div>
        <label className="check small">
          <input type="checkbox" checked={sendWelcome} onChange={(e) => setSendWelcome(e.target.checked)} /> <span>Willkommensmail mit Anmelde-Link schicken</span>
        </label>
        {saver.error && <div className="error">{saver.error}</div>}
      </form>
    </Card>
  );
}

function CustomerBasics({ customer, onChanged }: { customer: CustomerDetail; onChanged: (c: CustomerDetail) => void }) {
  const [form, setForm] = useState({ name: customer.name ?? '', email: customer.email });
  const saver = useSaver();
  useEffect(() => setForm({ name: customer.name ?? '', email: customer.email }), [customer]);
  const dirty = form.name !== (customer.name ?? '') || form.email !== customer.email;
  return (
    <Card
      as="form"
      title="Kundendaten"
      onSubmit={(e) => {
        e.preventDefault();
        void saver.run(async () => onChanged(await adminApi.updateCustomer(customer.id, { name: form.name || null, email: form.email })));
      }}
    >
      <div className="grid2">
        <Field label="Name" id="k-name">
          <input id="k-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={200} />
        </Field>
        <Field label="E-Mail" id="k-email" hint="Mit dieser Adresse meldet sich der Kunde an.">
          <input id="k-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        </Field>
      </div>
      <SaveBar saver={saver} dirty={dirty}>
        <button type="button" className="link-button link-button--danger" onClick={() => { if (confirmAction(`${customer.email} löschen? Alle Zugänge, Fortschritte und Notizen gehen verloren. Kaufbelege bleiben anonymisiert erhalten.`)) void adminApi.deleteCustomer(customer.id).then(() => navigate('/verwaltung/kunden')).catch((e: unknown) => saver.setError(errorText(e))); }}>
          Kunden löschen
        </button>
      </SaveBar>
    </Card>
  );
}
